import React, { useState, useEffect } from 'react';
import { canEdit, isAdminUser } from '../utils/permissions';
import { recallService } from '../services/recallService';
import { customerService } from '../services/customerService';
import { vehicleService } from '../services/vehicleService';
import { pdf } from '@react-pdf/renderer';
import RecallReportPdf from '../components/recall/RecallReportPdf';
import RecallAction from '../components/recall/RecallAction'; 
import VimsRecallImport from '../components/recall/VimsRecallImport';
import { toEmbeddableImage } from '../services/photoService';

if (typeof window !== 'undefined' && !window.Buffer) {
  window.Buffer = {
    isBuffer: () => false,
    from: () => [],
    alloc: () => [],
    concat: () => []
  };
}

const getLocalDateString = (dateObj = new Date()) => {
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const Recall = () => {
  const [executions, setExecutions] = useState([]);
  const [recallBases, setRecallBases] = useState([]); 
  const [allCustomers, setAllCustomers] = useState([]);
  const [allVehicles, setAllVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('LIST'); 
  const [isEditing, setIsEditing] = useState(false);

  const [showActionModal, setShowActionModal] = useState(false);
  const [showVimsImport, setShowVimsImport] = useState(false);   // VIMS 리콜 불러오기 창(2026-10-30)
  const [targetVehicle, setTargetVehicle] = useState(null);

  const initialForm = { id: null, baseId: '', title: '', modelName: '', reason: '', content: '', regDate: getLocalDateString() };
  const [formData, setFormData] = useState(initialForm);
  const [selectedExec, setSelectedExec] = useState(null);

  // 🚀 [신규 내장] PC 이행 차량 검색 및 페이지네이션 상태 제어
  const [vehSearch, setVehSearch] = useState('');
  const [vehPage, setVehPage] = useState(1);

  const refreshData = async () => {
    setLoading(true);
    try {
      const [execs, bases, customers, vehicles] = await Promise.all([
        recallService.getRecallExecutions(),
        recallService.getRecallBase(), 
        customerService.getCustomers(),
        vehicleService.getVehicles()
      ]);
      setExecutions(execs || []);
      setRecallBases(bases || []);
      setAllCustomers(customers || []);
      setAllVehicles(vehicles || []);

      if (selectedExec) {
        const updated = execs.find(e => e.id === selectedExec.id);
        if (updated) setSelectedExec(updated);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { refreshData(); }, []);

  const handleBaseSelect = (baseId) => {
    const selectedBase = recallBases.find(b => b.id === baseId);
    if (selectedBase) {
      setFormData({
        ...formData,
        baseId: selectedBase.id,
        title: selectedBase.title,
        modelName: selectedBase.modelName,
        reason: selectedBase.reason || "" 
      });
    }
  };

  // VIMS 에서 불러온 기초 데이터를 등록 화면에 바로 채운다(2026-10-30).
  const onVimsImported = async (base) => {
    setShowVimsImport(false);
    await refreshData();
    setFormData((f) => ({ ...f, baseId: base.id, title: base.title, modelName: base.modelName, reason: base.reason || '' }));
  };

  const onSaveCampaign = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.modelName) return alert("리콜 정책 데이터를 선택해 주세요.");

    setLoading(true);
    try {
      const targetVehs = allVehicles.filter(v => v.modelName === formData.modelName);
      const targets = targetVehs.map(v => {
        const owner = allCustomers.find(c => c.carNumber === v.carNumber) || {};
        const existingTarget = isEditing ? selectedExec?.targets?.find(t => t.carNumber === v.carNumber) : null;
        return {
          carNumber: v.carNumber,
          vin: v.vin || "",
          customerName: owner.name || '미등록',
          phone: owner.phone || '-',
          address: owner.address || '정보 없음',
          status: existingTarget ? existingTarget.status : '대기',
          actionData: existingTarget ? existingTarget.actionData : null,
          doneDate: existingTarget ? existingTarget.doneDate : null
        };
      });

      if (isEditing) {
        // 제자리 수정 — 삭제 후 재생성하면 중간 실패 시 캠페인이 통째로 사라진다.
        await recallService.updateRecallExecution(formData.id, formData, targets);
        alert("✅ 리콜 캠페인 정보와 명단이 최신화되었습니다.");
      } else {
        await recallService.startRecallExecution(formData, targets);
        alert("✅ 새 리콜 캠페인이 등록되었습니다.");
      }

      setFormData(initialForm);
      setIsEditing(false);
      setViewMode('LIST');
      await refreshData();
    } catch (err) { alert("❌ 저장 실패"); }
    setLoading(false);
  };

  const handleActionClick = (vehicle) => {
    setTargetVehicle(vehicle);
    setShowActionModal(true);
  };

  const handleActionSave = async (execId, carNumber, actionData) => {
    setLoading(true);
    try {
      await recallService.saveVehicleAction(execId, carNumber, actionData);
      alert("✅ 조치 내역이 저장되었습니다.");
      setShowActionModal(false);
      await refreshData();
    } catch (e) { alert("❌ 저장 실패"); }
    setLoading(false);
  };

  const handleEditCampaign = (item) => {
    setFormData(item);
    setSelectedExec(item);
    setIsEditing(true);
    setViewMode('REG');
  };

  const handleDelCampaign = async (id) => {
    if (window.confirm("정말 삭제할까요?")) {
      await recallService.remove(id);
      await refreshData();
    }
  };

  const handleDownload = async (exec, vehicle) => {
    try {
      // 사진은 이제 파일 보관소(Storage) 주소로 저장된다. PDF 생성기는 주소를 그대로 읽지 못할 수 있으므로
      // 먼저 이미지로 바꿔 넣는다. 못 읽은 사진은 건너뛰고 보고서 자체는 정상 생성한다.
      const srcList = (vehicle?.actionData?.photos) || [];
      const embedded = (await Promise.all(srcList.map(toEmbeddableImage))).filter(Boolean);
      const pdfVehicle = { ...vehicle, actionData: { ...(vehicle?.actionData || {}), photos: embedded } };

      const blob = await pdf(<RecallReportPdf exec={exec} vehicle={pdfVehicle} />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `리콜보고서_${vehicle.carNumber}.pdf`;
      link.click();
    } catch (err) {
      alert("PDF 보고서 다운로드 중 알 수 없는 시스템 제어 오류가 발생했습니다.");
    }
  };

  // 🚀 [데이터 프로세싱] 검색어 필터링 및 10대 단위 슬라이싱 가공 계산기
  const filteredTargets = (selectedExec?.targets || []).filter(t => 
    (t.carNumber || '').toLowerCase().includes(vehSearch.toLowerCase())
  );
  const itemsPerPage = 10;
  const totalPages = Math.ceil(filteredTargets.length / itemsPerPage);
  const paginatedTargets = filteredTargets.slice((vehPage - 1) * itemsPerPage, vehPage * itemsPerPage);

  const style = {
    wrapper: { padding: '50px', background: '#f8fafc', minHeight: '100vh' },
    card: { background: 'white', padding: '35px', borderRadius: '20px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', marginBottom: '25px' },
    btnPrimary: { padding: '12px 24px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer' },
    btnGhost: { padding: '8px 15px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', cursor: 'pointer', marginLeft: '5px' },
    input: { width: '100%', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '10px', marginTop: '8px' },
    statusBadge: (s) => ({ 
      padding: '4px 10px', 
      borderRadius: '6px', 
      fontSize: '12px', 
      background: s === '완료' ? '#f0fdf4' : '#fff7ed', 
      color: s === '완료' ? '#16a34a' : '#ea580c', 
      fontWeight: 'bold' 
    })
  };

  return (
    <div style={style.wrapper}>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '30px' }}>
        <button style={style.btnGhost} onClick={() => setViewMode('LIST')}>리콜 캠페인 목록</button>
        {canEdit() && <button style={style.btnPrimary} onClick={() => { setFormData(initialForm); setIsEditing(false); setViewMode('REG'); }}>+ 새 리콜 등록</button>}
      </div>

      {viewMode === 'LIST' && (
        <div style={style.card}>
          <h3 style={{marginBottom:'20px'}}>📋 리콜 캠페인 진행 현황</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '2px solid #f1f5f9', color: '#64748b' }}>
                <th style={{ padding: '15px' }}>등록일</th>
                <th>캠페인 명칭</th>
                <th>대상 차종</th>
                <th>진척도</th>
                <th style={{ textAlign: 'center' }}>관리</th>
              </tr>
            </thead>
            <tbody>
              {executions.map(e => {
                const total = e.targets?.length || 0;
                const done = e.targets?.filter(t => t.status === '완료').length || 0;
                const pct = total === 0 ? 0 : Math.round((done / total) * 100);
                return (
                  <tr key={e.id} style={{ borderBottom: '1px solid #f8fafc' }}>
                    <td style={{ padding: '15px' }}>{e.regDate || '날짜없음'}</td>
                    <td><b>{e.title}</b></td>
                    <td>{e.modelName}</td>
                    <td><b style={{color: pct === 100 ? '#16a34a' : '#ea580c'}}>{pct}%</b> ({done}/{total})</td>
                    <td style={{ textAlign: 'center' }}>
                      <button style={{...style.btnGhost, color:'#3b82f6'}} onClick={() => { setSelectedExec(e); setViewMode('DETAIL'); setVehSearch(''); setVehPage(1); }}>이행 관리</button>
                      <button style={style.btnGhost} onClick={() => handleEditCampaign(e)}>수정</button>
                      {canEdit() && <button style={{...style.btnGhost, color:'#ef4444'}} onClick={() => handleDelCampaign(e.id)}>삭제</button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {viewMode === 'REG' && (
        <div style={style.card}>
          <h3>{isEditing ? '🛠️ 리콜 캠페인 수정' : '📢 새 리콜 캠페인 등록'}</h3>
          <form onSubmit={onSaveCampaign} style={{ marginTop: '20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
              <div>
                <label style={{fontSize:'12px', fontWeight:'bold'}}>리콜 초기 데이터 선택</label>
                <select style={style.input} value={formData.baseId} onChange={e => handleBaseSelect(e.target.value)}>
                  <option value="">-- 정책 선택 --</option>
                  {recallBases.map(b => <option key={b.id} value={b.id}>{b.title} ({b.modelName}){b.source && b.source.system === 'VIMS' ? ' · VIMS' : ''}</option>)}
                </select>
                {/* 기초 데이터를 손으로 입력하는 대신 VIMS 리콜에서 가져온다. 기초 데이터는 관리자만 만들 수 있다(규칙). */}
                {isAdminUser() && !isEditing && (
                  <button type="button" onClick={() => setShowVimsImport(true)}
                    style={{ marginTop: '8px', padding: '7px 12px', background: '#fff', color: '#1e293b', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>
                    📥 VIMS에서 불러오기
                  </button>
                )}
              </div>
              <div>
                <label style={{fontSize:'12px', fontWeight:'bold'}}>캠페인 명칭</label>
                <input style={style.input} value={formData.title} readOnly />
              </div>
            </div>
            <div style={{marginBottom:'20px'}}>
              <label style={{fontSize:'12px', fontWeight:'bold'}}>대상 차종 모델</label>
              <input style={{...style.input, background:'#f8fafc'}} value={formData.modelName} readOnly />
            </div>
            <textarea style={{...style.input, height:'100px'}} placeholder="추가 전달 사항" value={formData.content} onChange={e => setFormData({...formData, content: e.target.value})} />
            <div style={{ marginTop: '30px' }}>
              {canEdit() && <button type="submit" style={{ ...style.btnPrimary, background: '#1e293b' }}>저장</button>}
              <button type="button" style={style.btnGhost} onClick={() => setViewMode('LIST')}>취소</button>
            </div>
          </form>
          {showVimsImport && (
            <VimsRecallImport existingBases={recallBases} onClose={() => setShowVimsImport(false)} onImported={onVimsImported} />
          )}
        </div>
      )}

      {viewMode === 'DETAIL' && selectedExec && (
        <div style={style.card}>
          {/* 🚀 [지시사항 수립] 타이틀 우측 동그라미 자리에 검색 영역 실장 */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3>🛠️ 실시간 이행 관리: {selectedExec.title}</h3>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <input 
                type="text" 
                placeholder="🔍 차량번호 입력 검색..." 
                value={vehSearch}
                onChange={e => { setVehSearch(e.target.value); setVehPage(1); }}
                style={{ width: '220px', padding: '10px 14px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13px', outline: 'none' }}
              />
              <button style={style.btnGhost} onClick={() => setViewMode('LIST')}>목록으로</button>
            </div>
          </div>
          
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f8fafc' }}>
              <tr style={{ textAlign: 'left', borderBottom: '2px solid #eee' }}>
                <th style={{ padding: '15px' }}>차량번호</th>
                <th>고객명</th>
                <th>주소</th>
                <th style={{color:'#3b82f6'}}>완료일</th>
                <th style={{color:'#3b82f6'}}>작업자</th>
                <th>상태</th>
                <th style={{ textAlign: 'center' }}>관리</th>
              </tr>
            </thead>
            <tbody>
              {paginatedTargets.map(t => (
                <tr key={t.carNumber} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '15px' }}><b>{t.carNumber}</b></td>
                  <td>{t.customerName}</td>
                  <td style={{ fontSize: '13px', color: '#64748b' }}>{t.address}</td>
                  <td style={{ fontSize: '13px' }}>{t.doneDate || '-'}</td>
                  <td style={{ fontSize: '13px' }}>{t.actionData?.engineer || '-'}</td>
                  <td><span style={style.statusBadge(t.status)}>{t.status}</span></td>
                  <td style={{ textAlign: 'center' }}>
                    {t.status === '완료' ? (
                      <div style={{display: 'flex', gap: '5px', justifyContent: 'center'}}>
                        <button style={style.btnGhost} onClick={() => handleDownload(selectedExec, t)}>보고서</button>
                        <button style={{...style.btnGhost, color:'#3b82f6'}} onClick={() => handleActionClick(t)}>수정</button>
                      </div>
                    ) : (
                      <button style={{...style.btnGhost, color: '#3b82f6'}} onClick={() => handleActionClick(t)}>조치하기</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* 🚀 [지시사항 수립] 한 페이지당 10대 구성 PC 제어 인터페이스 */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '5px', marginTop: '25px' }}>
              <button disabled={vehPage === 1} onClick={() => setVehPage(p => p - 1)} style={{ ...style.btnGhost, cursor: vehPage === 1 ? 'not-allowed' : 'pointer' }}>이전</button>
              {[...Array(totalPages)].map((_, i) => (
                <button 
                  key={i} 
                  onClick={() => setVehPage(i + 1)} 
                  style={{ 
                    ...style.btnGhost, 
                    background: vehPage === i + 1 ? '#1e293b' : '#ffffff', 
                    color: vehPage === i + 1 ? '#ffffff' : '#475569', 
                    fontWeight: 'bold',
                    border: '1px solid #cbd5e1'
                  }}
                >
                  {i + 1}
                </button>
              ))}
              <button disabled={vehPage === totalPages} onClick={() => setVehPage(p => p + 1)} style={{ ...style.btnGhost, cursor: vehPage === totalPages ? 'not-allowed' : 'pointer' }}>다음</button>
            </div>
          )}
        </div>
      )}

      {showActionModal && targetVehicle && (
        <RecallAction 
          execId={selectedExec.id} 
          vehicle={targetVehicle} 
          initialData={targetVehicle.actionData}
          onSave={handleActionSave} 
          onCancel={() => { setShowActionModal(false); setTargetVehicle(null); }} 
        />
      )}
    </div>
  );
};

export default Recall;